import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp, Search, Filter, Download, Plus, X,
  Mail, Phone, MessageSquare, Calendar, Tag, ChevronLeft, ChevronRight,
  User, Clock, Send, Trash2, Edit2, Check, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { api } from '../../lib/api';
import { useLanguage } from '../../context/LanguageContext';

const CHANNEL_OPTIONS = [
  { value: 'web', label: 'Web' },
  { value: 'widget', label: 'Widget' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'instagram', label: 'Instagram' },
];

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, novo: 0, contactado: 0, qualificado: 0, convertido: 0 });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  // Selected lead for details modal
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Notes
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Tags editing
  const [editingTags, setEditingTags] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const { t, language } = useLanguage();

  const STATUS_OPTIONS = [
    { value: 'novo', label: t('leads.statusNew'), color: 'bg-blue-100 text-blue-800' },
    { value: 'contactado', label: t('leads.statusContacted'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'qualificado', label: t('leads.statusQualified'), color: 'bg-purple-100 text-purple-800' },
    { value: 'convertido', label: t('leads.statusConverted'), color: 'bg-green-100 text-green-800' },
  ];

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (channelFilter) params.append('channel', channelFilter);

      const response = await api.get(`/api/leads?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
        setTotal(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, channelFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/api/leads/stats');
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
    fetchStats();
  }, [fetchLeads, fetchStats]);

  const fetchLeadDetails = async (contactId) => {
    setDetailsLoading(true);
    try {
      const response = await api.get(`/api/leads/${contactId}`);
      if (response.ok) {
        const data = await response.json();
        setLeadDetails(data);
      }
    } catch (error) {
      console.error('Failed to fetch lead details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    fetchLeadDetails(lead.contact_id);
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      const response = await api.patch(`/api/leads/${contactId}`, { status: newStatus });
      if (response.ok) {
        // Update local state
        setLeads(leads.map(l =>
          l.contact_id === contactId ? { ...l, status: newStatus } : l
        ));
        if (leadDetails?.lead?.contact_id === contactId) {
          setLeadDetails({
            ...leadDetails,
            lead: { ...leadDetails.lead, status: newStatus }
          });
        }
        fetchStats();
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedLead) return;
    setAddingNote(true);
    try {
      const response = await api.post(`/api/leads/${selectedLead.contact_id}/notes`, {
        content: newNote.trim()
      });
      if (response.ok) {
        const note = await response.json();
        setLeadDetails({
          ...leadDetails,
          notes: [note, ...(leadDetails.notes || [])]
        });
        setNewNote('');
      }
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setAddingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!selectedLead || !window.confirm(t('leads.deleteNote'))) return;
    try {
      const response = await api.delete(`/api/leads/${selectedLead.contact_id}/notes/${noteId}`);
      if (response.ok) {
        setLeadDetails({
          ...leadDetails,
          notes: leadDetails.notes.filter(n => n.id !== noteId)
        });
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleAddTag = async () => {
    if (!tagInput.trim() || !selectedLead) return;
    const currentTags = leadDetails?.lead?.tags || [];
    const newTags = [...currentTags, tagInput.trim()];

    try {
      const response = await api.patch(`/api/leads/${selectedLead.contact_id}`, {
        tags: newTags
      });
      if (response.ok) {
        setLeadDetails({
          ...leadDetails,
          lead: { ...leadDetails.lead, tags: newTags }
        });
        setLeads(leads.map(l =>
          l.contact_id === selectedLead.contact_id ? { ...l, tags: newTags } : l
        ));
        setTagInput('');
      }
    } catch (error) {
      console.error('Failed to add tag:', error);
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    if (!selectedLead) return;
    const currentTags = leadDetails?.lead?.tags || [];
    const newTags = currentTags.filter(t => t !== tagToRemove);

    try {
      const response = await api.patch(`/api/leads/${selectedLead.contact_id}`, {
        tags: newTags
      });
      if (response.ok) {
        setLeadDetails({
          ...leadDetails,
          lead: { ...leadDetails.lead, tags: newTags }
        });
        setLeads(leads.map(l =>
          l.contact_id === selectedLead.contact_id ? { ...l, tags: newTags } : l
        ));
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (channelFilter) params.append('channel', channelFilter);

      const response = await api.get(`/api/leads/export/csv?${params.toString()}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'leads.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    return <Badge className={option.color}>{option.label}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString(language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('leads.title')}</h1>
          <p className="text-gray-500">{t('leads.subtitle')}</p>
        </div>
        <Button onClick={handleExport} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          {t('leads.exportCSV')}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-500">Total</div>
          </CardContent>
        </Card>
        {STATUS_OPTIONS.map(status => (
          <Card key={status.value} className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setStatusFilter(statusFilter === status.value ? '' : status.value)}>
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-900">{stats[status.value] || 0}</div>
              <div className="text-sm text-gray-500">{status.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                placeholder={t('leads.searchPlaceholder')}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('common.all')}</SelectItem>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={channelFilter} onValueChange={(v) => { setChannelFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Canal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">{t('common.all')}</SelectItem>
                  {CHANNEL_OPTIONS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">{t('common.loading')}</div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <TrendingUp className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">{t('leads.noLeads')}</h3>
              <p className="text-gray-500">{t('leads.noLeadsDesc')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600">{t('common.name')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('common.contact')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('common.channel')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('common.status')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('common.tags')}</th>
                    <th className="text-left p-4 font-medium text-gray-600">{t('common.date')}</th>
                    <th className="text-left p-4 font-medium text-gray-600"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr
                      key={lead.contact_id}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleLeadClick(lead)}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-medium">
                            {lead.name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="font-medium text-gray-900">{lead.name}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          {lead.email && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Mail className="h-3 w-3" />
                              {lead.email}
                            </div>
                          )}
                          {lead.phone && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <Phone className="h-3 w-3" />
                              {lead.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">{lead.channel || 'web'}</Badge>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <Select
                          value={lead.status || 'novo'}
                          onValueChange={(v) => handleStatusChange(lead.contact_id, v)}
                        >
                          <SelectTrigger className="w-[130px] h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUS_OPTIONS.map(s => (
                              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {(lead.tags || []).slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                          ))}
                          {(lead.tags || []).length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{lead.tags.length - 2}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-500">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem onClick={() => handleLeadClick(lead)}>
                              {t('leads.viewDetails')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleStatusChange(lead.contact_id, 'contactado')}>
                              {t('leads.markAsContacted')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-gray-500">
                {t('leads.showing')} {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} {t('leads.of')} {total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="flex items-center px-3 text-sm">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lead Details Modal */}
      <Dialog open={!!selectedLead} onOpenChange={(open) => !open && setSelectedLead(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[#3b82f6] rounded-full flex items-center justify-center text-white font-medium text-xl">
                {selectedLead?.name?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="text-xl">{selectedLead?.name}</div>
                <div className="text-sm font-normal text-gray-500">
                  {selectedLead?.channel || 'web'} - {formatDate(selectedLead?.created_at)}
                </div>
              </div>
            </DialogTitle>
          </DialogHeader>

          {detailsLoading ? (
            <div className="text-center py-8">{t('common.loading')}</div>
          ) : leadDetails && (
            <div className="space-y-6 mt-4">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('common.email')}</Label>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{leadDetails.lead?.email || '-'}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">{t('common.phone')}</Label>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{leadDetails.lead?.phone || '-'}</span>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label className="text-gray-500">{t('common.status')}</Label>
                <Select
                  value={leadDetails.lead?.status || 'novo'}
                  onValueChange={(v) => handleStatusChange(selectedLead.contact_id, v)}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tags */}
              <div className="space-y-2">
                <Label className="text-gray-500">{t('common.tags')}</Label>
                <div className="flex flex-wrap gap-2">
                  {(leadDetails.lead?.tags || []).map((tag, i) => (
                    <Badge key={i} variant="secondary" className="gap-1">
                      {tag}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-red-500"
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  ))}
                  <div className="flex gap-1">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      placeholder={t('leads.newTag')}
                      className="h-7 w-24 text-sm"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                    />
                    <Button size="sm" variant="outline" className="h-7" onClick={handleAddTag}>
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-3">
                <Label className="text-gray-500">{t('common.notes')}</Label>
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={t('leads.addNote')}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                  />
                  <Button onClick={handleAddNote} disabled={addingNote || !newNote.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {(leadDetails.notes || []).map((note) => (
                    <div key={note.id} className="bg-gray-50 rounded-lg p-3 flex justify-between">
                      <div>
                        <p className="text-sm">{note.content}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatDate(note.created_at)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 h-6"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {(leadDetails.notes || []).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-2">{t('leads.noNotes')}</p>
                  )}
                </div>
              </div>

              {/* Conversation History */}
              <div className="space-y-3">
                <Label className="text-gray-500 flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  {t('leads.conversationHistory')} ({leadDetails.conversations?.length || 0})
                </Label>
                <div className="space-y-4 max-h-60 overflow-y-auto">
                  {(leadDetails.conversations || []).map((conv) => (
                    <div key={conv.conversation_id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">
                          {conv.channel} - {formatDate(conv.created_at)}
                        </span>
                        <Badge variant="outline">{conv.status}</Badge>
                      </div>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {(conv.messages || []).slice(-5).map((msg, i) => (
                          <div
                            key={i}
                            className={`text-sm p-2 rounded ${
                              msg.role === 'user'
                                ? 'bg-blue-50 text-blue-800'
                                : 'bg-gray-50 text-gray-800'
                            }`}
                          >
                            <span className="font-medium">
                              {msg.role === 'user' ? 'Lead: ' : 'Bot: '}
                            </span>
                            {msg.content}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  {(leadDetails.conversations || []).length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-2">{t('leads.noConversations')}</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadsPage;
